import * as React from 'react'

import {
  WorkScheduleOverlapPeriodsCrumbDocument,
  WorkSchedulesCrumbDocument,
  WorkSchedulesRootPageDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const WorkScheduleRootPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/WorkScheduleRootPage')
)

const WorkSchedulesRootPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/WorkSchedulesRootPage')
)

const AddEmployeeModalPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/AddEmployeeModalPage')
)
const AddPeriodPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/AddPeriodPage')
)

const AddWorkScheduleModalPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/AddWorkScheduleModalPage')
)

const ArchiveWorkScheduleModalPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/ArchiveWorkScheduleModalPage')
)

const EditPeriodPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/EditPeriodPage')
)

const RedirectToWorkScheduleSettingsPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/RedirectToWorkScheduleSettingsPage')
)

const WorkScheduleEditPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/WorkScheduleEditPage')
)

const WorkScheduleEmployeePage = React.lazy(
  () => import('modules/work_schedules/pages/settings/WorkScheduleEmployeePage')
)

const ArchivedWorkSchedulesPage = React.lazy(
  () => import('modules/work_schedules/pages/settings/ArchivedWorkedSchedulesPage')
)

const RootPage = React.lazy(() => import('modules/work_schedules/pages/settings/RootPage'))

/**
 * Converts a 1-based month number (1 = January, 12 = December) to a month name abbreviation.
 * This is needed because moment.js uses 0-based months (0 = January, 11 = December),
 * while the backend stores months as 1-based integers.
 *
 * @param month - Month number from backend (1-12)
 * @returns Month name abbreviation (e.g., "Jan", "Feb", "Mar")
 */
export const monthName = (month: number): string => {
  return moment()
    .month(month - 1)
    .format('MMM')
}

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.workSchedule.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, navigate }) => {
          return watchQuery({
            query: WorkSchedulesRootPageDocument,
          }).map(({ data }) => {
            const manageWorkSchedules =
              data?.workSchedule?._policies?.manageCompanySchedules?.can ?? false

            if (!manageWorkSchedules) {
              navigate(navigation.settings.fullPath())
            }

            return getSettingPageMeta(navigation, 'work_schedules')
          })
        },
      },
      children: [
        {
          handle: {
            meta: () => ({
              subNavigation: [
                {
                  index: true,
                  title: i18n.t('settings.work_schedule.title'),
                  href: navigation.settings.workSchedule.fullPath(),
                },
                {
                  title: i18n.t('settings.work_schedule.archived'),
                  href: navigation.settings.workSchedule.archived.fullPath(),
                },
              ],
            }),
          },
          children: [
            {
              index: true,
              element: <WorkSchedulesRootPage />,
            },
            {
              path: navigation.settings.workSchedule.new.path,
              element: <AddWorkScheduleModalPage />,
            },
            {
              path: navigation.settings.workSchedule.id.path,
              children: [
                {
                  path: navigation.settings.workSchedule.id.archive.path,
                  element: <ArchiveWorkScheduleModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.workSchedule.archived.path,
              element: <ArchivedWorkSchedulesPage />,
            },
          ],
        },
        {
          path: navigation.settings.workSchedule.id.path,
          element: <WorkScheduleRootPage />,
          handle: {
            meta: ({ params, watchQuery }) => {
              const id = params['id']
              if (!id) return { title: '' }

              return watchQuery({
                query: WorkSchedulesCrumbDocument,
                fetchPolicy: 'cache-first',
                variables: {
                  id: String(id),
                },
              }).map((result) => ({
                title: `${result?.data?.workSchedule?.schedule?.name}`,
              }))
            },
          },
          children: [
            {
              index: true,
              element: <RedirectToWorkScheduleSettingsPage />,
            },
            {
              path: navigation.settings.workSchedule.id.edit.path,
              element: <WorkScheduleEditPage />,
              children: [],
            },
            {
              path: navigation.settings.workSchedule.id.periodNew.path,
              element: <AddPeriodPage />,
              handle: {
                meta: () => {
                  return {
                    title: i18n.t('settings.work_schedule.period_section.add_period_modal.title'),
                  }
                },
              },
            },
            {
              path: navigation.settings.workSchedule.id.periodPeriodId.path,
              element: <EditPeriodPage />,
              handle: {
                meta: ({ params, watchQuery }) => {
                  const id = params['periodId']
                  if (!id) return { title: '' }

                  return watchQuery({
                    query: WorkScheduleOverlapPeriodsCrumbDocument,
                    fetchPolicy: 'cache-first',
                    variables: {
                      ids: [String(id)],
                    },
                  }).map((result) => {
                    const overlapPeriod =
                      result?.data?.workSchedule?.overlapPeriodsConnection?.edges?.[0]?.node
                    if (!overlapPeriod) return { title: '' }

                    return {
                      title: i18n.t('settings.work_schedule.period_section.period_title', {
                        startDay: overlapPeriod.startDay,
                        startMonth: monthName(overlapPeriod.startMonth),
                        endDay: overlapPeriod.endDay,
                        endMonth: monthName(overlapPeriod.endMonth),
                      }),
                    }
                  })
                },
              },
            },
            {
              path: navigation.settings.workSchedule.id.employees.path,
              element: <WorkScheduleEmployeePage />,
              children: [
                {
                  path: navigation.settings.workSchedule.id.employees.add.path,
                  element: <AddEmployeeModalPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
